import { Component } from "@theme/component"

export class CustomerReviews extends Component {
  connectedCallback() {
    super.connectedCallback()
    
    this.shopId = this.dataset.shopId
    this.page = 1
    this.sortBy = "date"
    this.sortDirection = "desc"
    this.loading = false
    
    this.loadReviews()
  }

  async loadReviews() {
    if (this.loading || !this.shopId) return
    this.loading = true
    
    if (this.refs.list) {
      this.refs.list.style.opacity = '0.5'
      this.refs.list.style.pointerEvents = 'none'
    }

    try {
      const url = new URL(`https://api.nufs.app/shopify/review`)
      url.searchParams.set("shopId", this.shopId)
      url.searchParams.set("page", this.page.toString())
      url.searchParams.set("sortBy", this.sortBy)
      url.searchParams.set("sortDirection", this.sortDirection)

      const response = await fetch(url.toString())
      if (!response.ok) throw new Error("Failed to fetch reviews")
      
      const { data, meta } = await response.json()
      
      this.renderSummary(meta)
      this.renderList(data)
      this.renderPagination(meta)
      
      if (this.refs.empty) {
        this.refs.empty.style.display = data.length === 0 ? "block" : "none"
      }
      if (this.refs.summary) {
        this.refs.summary.style.display = data.length === 0 ? "none" : ""
      }
      if (this.refs.list) {
        this.refs.list.style.display = data.length === 0 ? "none" : "flex"
      }

    } catch (err) {
      console.error("Error loading reviews:", err)
    } finally {
      this.loading = false
      if (this.refs.list) {
        this.refs.list.style.opacity = '1'
        this.refs.list.style.pointerEvents = ''
      }
    }
  }

  changePage(page, event) {
    if (event) event.preventDefault()
    this.page = page
    this.loadReviews()
    // Scroll to top of reviews section
    this.scrollIntoView({ behavior: 'smooth' })
  }

  openLightbox(event) {
    if (event) event.preventDefault()
    const url = event.target.closest('[data-url]')?.dataset?.url
    if (url && this.refs.lightbox && this.refs.lightboxImg) {
      this.refs.lightboxImg.src = url
      this.refs.lightbox.classList.add('is-open')
      this.refs.lightbox.setAttribute('aria-hidden', 'false')
      document.body.style.overflow = 'hidden'
    }
  }

  closeLightbox(event) {
    if (event) event.preventDefault()
    if (this.refs.lightbox) {
      this.refs.lightbox.classList.remove('is-open')
      this.refs.lightbox.setAttribute('aria-hidden', 'true')
      document.body.style.overflow = ''
      setTimeout(() => {
        if (this.refs.lightboxImg) this.refs.lightboxImg.src = ''
      }, 300)
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.refs.lightbox && this.refs.lightbox.classList.contains('is-open')) {
      this.closeLightbox()
    }
  }

  // ---- Rendering logic ----
  
  renderSummary(meta) {
    if (!meta) return
    
    if (this.refs.avgDisplay) {
      const avg = meta.ratingAverage || 0
      this.refs.avgDisplay.textContent = avg.toFixed(1)
    }
    
    if (this.refs.avgCount) {
      this.refs.avgCount.textContent = `${meta.totalCount} yorum`
    }
    
    if (this.refs.avgStars) {
      const avg = meta.ratingAverage || 0
      const floor = Math.floor(avg)
      const decimal = avg - floor
      let html = ''
      for (let i = 1; i <= 5; i++) {
        if (i <= floor) {
          html += this.getStarSvg('filled')
        } else if (i === floor + 1 && decimal > 0) {
          html += this.getStarPartialSvg(decimal, `grad-avg-${this.dataset.sectionId}`)
        } else {
          html += this.getStarSvg('empty')
        }
      }
      this.refs.avgStars.innerHTML = html
    }
    
    if (this.refs.dist) {
      const dist = meta.ratingDistribution || {5:0, 4:0, 3:0, 2:0, 1:0}
      const total = meta.totalCount || 1 
      let html = ''
      ;[5, 4, 3, 2, 1].forEach(level => {
        const count = dist[level] || 0
        const pct = ((count / total) * 100).toFixed(1)
        html += `
          <div class="cr-dist-row">
            <span class="cr-dist-row__label">
              ${level}&nbsp;
              ${this.getStarSvg('filled', 'cr-star--xs')}
            </span>
            <div class="cr-dist-row__track">
              <div class="cr-dist-row__fill" style="--fill: ${pct}%"></div>
            </div>
            <span class="cr-dist-row__count">${count}</span>
          </div>
        `
      })
      this.refs.dist.innerHTML = html
    }
  }

  renderList(data) {
    if (!this.refs.list) return
    if (!data || data.length === 0) {
      this.refs.list.innerHTML = ''
      return
    }
    
    let html = ''
    data.forEach((review, index) => {
      const initial = (review.customer || 'A').charAt(0).toUpperCase()
      const rating = review.rating || 0
      
      let dateStr = ''
      if (review.date || review.updatedAt) {
        const d = new Date(review.date || review.updatedAt)
        const months = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']
        dateStr = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }

      let starsHtml = ''
      for (let i=1; i<=5; i++) {
        starsHtml += i <= rating ? this.getStarSvg('filled') : this.getStarSvg('empty')
      }

      let mediaHtml = ''
      if (review.media && review.media.length > 0) {
        mediaHtml = `<div class="cr-card__media">`
        review.media.forEach(img => {
          mediaHtml += `
            <button
              type="button"
              class="cr-card__thumb"
              data-url="${img.url}"
              on:click="/openLightbox"
              aria-label="Görseli büyüt"
            >
              <img src="${img.url}&width=240&height=240&crop=center" alt="Yorum görseli" loading="lazy" width="80" height="80">
            </button>
          `
        })
        mediaHtml += `</div>`
      }

      html += `
        <article class="cr-card is-visible" style="--delay: ${index * 60}ms; opacity: 1; transform: none;">
          <div class="cr-card__header">
            <div class="cr-card__author">
              <span class="cr-card__avatar" aria-hidden="true">${initial}</span>
              <div class="cr-card__author-info">
                <span class="cr-card__name">${review.customer || 'Anonim'}</span>
                ${review.platform ? `<span class="cr-card__platform"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>${review.platform}</span>` : ''}
              </div>
            </div>
            <div class="cr-card__rating-date">
              <div class="cr-card__stars">${starsHtml}</div>
              <time class="cr-card__date">${dateStr}</time>
            </div>
          </div>
          ${review.content ? `<div class="cr-card__body">${review.content.replace(/\\n/g, '<br>')}</div>` : ''}
          ${mediaHtml}
        </article>
      `
    })
    this.refs.list.innerHTML = html
  }

  renderPagination(meta) {
    if (!this.refs.pagination) return
    
    if (!meta || meta.totalPages <= 1) {
      this.refs.pagination.innerHTML = ''
      return
    }

    const { page, totalPages } = meta
    let html = '<nav class="cr-pagination" aria-label="Yorum sayfaları">'

    if (page > 1) {
      html += `<button type="button" class="cr-pagination__btn" on:click="/changePage/${page - 1}"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/></svg></button>`
    }

    for (let i = 1; i <= totalPages; i++) {
      const isActive = i === page
      const classNames = isActive ? 'cr-pagination__num cr-pagination__num--active' : 'cr-pagination__num'
      html += `<button type="button" class="${classNames}" ${isActive ? '' : `on:click="/changePage/${i}"`}>${i}</button>`
    }

    if (page < totalPages) {
      html += `<button type="button" class="cr-pagination__btn" on:click="/changePage/${page + 1}"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/></svg></button>`
    }
    
    html += '</nav>'
    this.refs.pagination.innerHTML = html
  }

  getStarSvg(type, extraClass = '') {
    if (type === 'filled') {
      return `<svg class="cr-star cr-star--filled ${extraClass}" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`
    }
    return `<svg class="cr-star cr-star--empty ${extraClass}" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`
  }

  getStarPartialSvg(decimal, gradId) {
    const pct = decimal * 100
    return `
      <svg class="cr-star cr-star--partial" viewBox="0 0 24 24" aria-hidden="true">
        <defs><linearGradient id="${gradId}"><stop offset="${pct}%" stop-color="var(--cr-star-color)"/><stop offset="${pct}%" stop-color="var(--cr-star-empty)"/></linearGradient></defs>
        <path fill="url(#${gradId})" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    `
  }
}

if (!customElements.get("customer-reviews")) {
  customElements.define("customer-reviews", CustomerReviews)
}
